/* eslint-disable react/jsx-props-no-spreading */
import React, {useState, useEffect} from 'react';
import App, {AppContext} from 'next/app';

import './globalStyles.css';

import acceptLanguage from 'accept-language';
import {ThemeProvider, JssProvider, SheetsRegistry} from 'react-jss';
import Head from 'next/head';
import Cookies from 'js-cookie';
import {notification, Spin} from 'antd';
import AntConfigProvider from '@components/ant-config-provider';
import {autorun} from 'mobx';
import equal from 'deep-equal';
import {Provider, Observer} from 'mobx-react';
import initializeStore from '@utils/mobx';
import setLanguage from '@utils/i18n';
import {I18nextProvider} from 'react-i18next';
import languageApi from '@utils/api/language.api';
import translationApi from '@utils/api/translation.api';
import Layout from '@components/layout';
import {getTheme, getCurrentStyleFormat} from '@utils/theme';
import {useDebounceFn} from 'ahooks';
import {WagmiProvider} from 'wagmi';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import WalletConnectConfig from '@configs/WalletConnectConfig';
import {ErrorBoundary} from 'react-error-boundary';
import 'mobx-react-lite/batchingForReactDom';
import {env} from 'next-runtime-env';
import {TonConnectUIProvider} from '@tonconnect/ui-react';
import '../../scripts/why-did-you-render/injection';
import {isProd, productionURL} from '@components/deposit-withdraw/TelegramContent';
import {isEmbedded} from '@utils/gateway/merchant-gateway';
import {debounce} from 'lodash-es';
import {useRouter} from 'next/router';

const {TrackJS} = process.browser ? require('trackjs') : require('trackjs-node');

const NEXT_PUBLIC_RUNTIME_ENV = env('NEXT_PUBLIC_RUNTIME_ENV');
const NEXT_PUBLIC_WEB_TYPE = env('NEXT_PUBLIC_WEB_TYPE');
const NEXT_PUBLIC_FRONTEND_HOST_URL = env('NEXT_PUBLIC_FRONTEND_HOST_URL');
// const manifestUrl = `${NEXT_PUBLIC_FRONTEND_HOST_URL}/tonconnect-manifest.json`;
// * have to align with tonconnect-manifest.json
const staging = 'https://poker.k8s-new-stage.bull-b.com';

// TODO @@ manifestUrl for staging and production
const manifestUrl = isProd
  ? `${productionURL}/tonconnect-manifest.json`
  : `${staging}/tonconnect-manifest.json`;

const twaReturnUrl = isProd ? 'https://t.me/Win4PokerBot' : 'https://t.me/devTgRandomBot';

console.log('_app - manifestUrl:', manifestUrl);
// console.log('NEXT_PUBLIC_WEB_TYPE', NEXT_PUBLIC_WEB_TYPE);

const isMobxReady = mobxStore => {
  for (const key of Object.keys(mobxStore)) {
    if (!mobxStore[key].isReady) {
      return false;
    }
  }
  return true;
};

if (!TrackJS.isInstalled()) {
  TrackJS.install({
    token: 'a95d26cdc3364b00bab28965a2e18050',
  });
}

// Layout wrapper component to conditionally apply layout
const LayoutWrapper = ({Component, pageProps, isFlipPhoneOuterScreen}) => {
  const router = useRouter();
  
  // List of routes that should not have the default layout
  const noLayoutRoutes = [
    '/gateway-login',
    '/gateway-player-login',
    '/gateway-player-register',
    '/merchant-login',
  ];
  
  // Check if current route should have layout
  const shouldUseLayout = !noLayoutRoutes.includes(router.pathname);
  
  if (shouldUseLayout) {
    return (
      <Layout>
        <Head>
          <title>Poker Game</title>
          <meta
            name="viewport"
            content={
              isFlipPhoneOuterScreen
                ? 'width=300, height=700, initial-scale=0.4, maximum-scale=1.0,user-scalable=0'
                : 'width=device-width, initial-scale=1.0, maximum-scale=1.0,user-scalable=0'
            }
          />
        </Head>
        <Component {...pageProps} />
      </Layout>
    );
  }
  
  // Render without layout for gateway pages
  return (
    <>
      <Head>
        <title>Poker Game</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0,user-scalable=0"
        />
      </Head>
      <Component {...pageProps} />
    </>
  );
};

// eslint-disable-next-line
const MyApp = ({Component, pageProps, initialMobxState, i18nResource, currentLanguage}) => {
  const isServer = typeof window === 'undefined';
  const mobxStore = isServer ? initialMobxState : initializeStore(initialMobxState);
  const [i18n, setI18n] = useState(
    i18nResource ? setLanguage(currentLanguage, i18nResource) : null
  );
  const [styleFormat, setStyleFormat] = useState(null);
  const [userTheme, setUserTheme] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isFlipPhoneOuterScreen, setIsFlipPhoneOuterScreen] = useState<boolean>(false);

  const {run: resizeListener} = useDebounceFn(
    () => {
      const currentStyleFormat = getCurrentStyleFormat(window.innerWidth);
      if (currentStyleFormat && !equal(currentStyleFormat, styleFormat)) {
        setStyleFormat(currentStyleFormat);
      }
    },
    {wait: 100}
  );

  useEffect(() => {
    const fetchI18n = async () => {
      if (!i18n) {
        const translationSources = await translationApi.getTranslationSource(NEXT_PUBLIC_WEB_TYPE);
        setI18n(setLanguage(currentLanguage, translationSources));
      }
    };
    resizeListener();
    window.addEventListener('resize', resizeListener);
    fetchI18n();

    i18n.changeLanguage(mobxStore.language.currentLanguage);
    if (!isServer) {
      Cookies.set('currentLanguage', mobxStore.language.currentLanguage, {
        expires: 7,
      });
      if (NEXT_PUBLIC_RUNTIME_ENV === 'production') {
        // TODO @@ un-comment below when bugs are fixed
        // console.log = function () {};
      }
    }
    TrackJS.track('Testing TrackJS!');

    const jssStyles = document.querySelector('#server-side-styles');
    if (jssStyles && jssStyles.parentNode) {
      jssStyles.parentNode.removeChild(jssStyles);
    }

    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, []);

  // * this fixed hydration error
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // When running inside the 88x merchant iframe, lift fixed-bottom UI off
  // the area covered by their bottom bar. Height is configurable via
  // NEXT_PUBLIC_MERCHANT_BOTTOM_INSET (fallback: 64px).
  useEffect(() => {
    if (typeof window === 'undefined' || !isEmbedded()) return;
    const inset = env('NEXT_PUBLIC_MERCHANT_BOTTOM_INSET') || '64';
    document.documentElement.style.setProperty('--merchant-bottom-inset', `${inset}px`);
  }, []);

  // Watch for language changes and update i18n
  useEffect(() => {
    if (!i18n || !mobxStore?.language) return;
    
    const disposer = autorun(() => {
      const currentLang = mobxStore.language.currentLanguage;
      if (currentLang && i18n.language !== currentLang) {
        console.log('Language changed to:', currentLang);
        i18n.changeLanguage(currentLang);
        if (!isServer) {
          Cookies.set('currentLanguage', currentLang, {
            expires: 7,
          });
        }
      }
    });

    return () => {
      disposer();
    };
  }, [i18n, mobxStore?.language]);

  useEffect(() => {
    const checkScreenHeight = () => {
      const viewportHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;
      console.log('viewportHeight', viewportHeight);
      if (viewportHeight) {
        setIsFlipPhoneOuterScreen(viewportHeight < 400);
      }
    };

    // Create debounced version of the check function
    const debouncedCheckScreenHeight = debounce(checkScreenHeight, 300); // Reduced from 1050ms

    // Use the debounced version for both initial check and resize
    debouncedCheckScreenHeight();

    // window.addEventListener('resize', debouncedCheckScreenHeight);

    // return () => {
    //   window.removeEventListener('resize', debouncedCheckScreenHeight);
    //   debouncedCheckScreenHeight.cancel(); // Cancel any pending debounced calls
    // };
  }, []);

  if (!isMounted) {
    return null;
  }

  // 0. Setup queryClient
  const queryClient = new QueryClient();

  // 1. Get projectId from https://cloud.walletconnect.com
  // const projectId = NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID;

  // const siweConfig = createSIWE(true);

  //createWeb3Modal({wagmiConfig: WalletConnectConfig, projectId, siweConfig});

  const logError = (error: Error, info: {componentStack: string}) => {
    // Do something with the error, e.g. log to an external API
    // console.log('errorBoundary error, info', error, info);
    TrackJS.track(error);
  };

  return (
    <ErrorBoundary onError={logError} fallbackRender={null}>
      {/* <UseRequestProvider
        value={{
          onError: err => {
            const errMessage = err?.response?.data?.message;
            const errDescription = err?.response?.data?.extras?.errors;
            // TODO @@ do not show error message in production
            notification.error({ message: errMessage, description: errDescription });
          },
          loadingDelay: 500,
        }}
      > */}

      <Provider {...mobxStore}>
        <I18nextProvider i18n={i18n}>
          <AntConfigProvider>
            <ThemeProvider theme={getTheme(styleFormat, userTheme)}>
              <WagmiProvider config={WalletConnectConfig}>
                <TonConnectUIProvider
                  manifestUrl={manifestUrl}
                  actionsConfiguration={{
                    // twaReturnUrl: 'https://t.me/devTgRandomBot/devTgRandom',
                    twaReturnUrl,
                  }}
                >
                  <QueryClientProvider client={queryClient}>
                    {/* <MetaMaskProvider
                    debug
                    sdkOptions={{
                      dappMetadata: {
                        name: 'Web3 Poker',
                        url: NEXT_PUBLIC_FRONTEND_HOST_URL,
                      },
                      infuraAPIKey: process.env.INFURA_API_KEY,
                      // Other options.
                    }}
                  > */}
                    <LayoutWrapper Component={Component} pageProps={pageProps} isFlipPhoneOuterScreen={isFlipPhoneOuterScreen} />
                    {/* </MetaMaskProvider> */}
                  </QueryClientProvider>
                </TonConnectUIProvider>
              </WagmiProvider>
            </ThemeProvider>
          </AntConfigProvider>
        </I18nextProvider>
      </Provider>
      {/* </UseRequestProvider> */}
    </ErrorBoundary>
  );
};

MyApp.getInitialProps = async (appContext: AppContext) => {
  const isServer = typeof window === 'undefined';

  let pageProps = {};
  if (App.getInitialProps) {
    pageProps = await App.getInitialProps(appContext);
  }

  // const publicConfigs = {};
  // for (const key of Object.keys(process.env)) {
  //   if (key.indexOf('NEXT_PUBLIC_') === 0) {
  //     publicConfigs[key] = process.env[key];
  //   }
  // }

  // console.log('publicConfigs', publicConfigs)

  // console.log('setConfig',setConfig)

  // setConfig({ runtimeConfig: publicConfigs })

  if (isServer) {
    const {ctx} = appContext;
    // Get translation resources

    let i18nResource = null;
    let languages = [];
    try {
      [i18nResource, languages] = await Promise.all([
        translationApi.getTranslationSource(NEXT_PUBLIC_WEB_TYPE),
        languageApi.getLanguages(),
      ]);
    } catch (error) {}
    // Get supported languages from backend

    // Get current language from cookies
    // * prev: default en-US, now: base on user's IP
    // let currentLanguage = 'en-US';
    let currentLanguage = '';

    if (!currentLanguage) {
      // Get current language from browser
      const languageIDs = languages.map(language => language.id);

      if (languageIDs.length) {
        acceptLanguage.languages(languageIDs);
      }
      if (ctx.req) {
        currentLanguage = acceptLanguage.get(ctx.req.headers['accept-language']);
      }
    }
    // Set Support languages and current language to mobx
    const mobxStore = initializeStore({
      language: {
        supportLanguages: languages,
        currentLanguage,
      },
    });
    // ctx.mobxStore = mobxStore;
    return {
      pageProps,
      initialMobxState: mobxStore,
      i18nResource,
      currentLanguage,
    };
  }
  return {...pageProps};
};

export default MyApp;
